import React, { useEffect, useCallback } from 'react';
import useStyles from 'isomorphic-style-loader/useStyles';
import { useQuery } from '@apollo/client';
import { useSelector } from 'react-redux';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { useInViewRef } from 'rooks';

// app: components
import BookTile from 'components/BookTile';
import BookListTile from 'components/SearchResultsItem/Collection/BookItem';
import EmptyShelf from 'components/EmptyShelf';
import ErrorRouter from 'components/ErrorRouter';

// app modules: hooks
import useFeatureToggle from 'hooks/useFeatureToggle';

// app: services
import { isApp } from 'services/environment';
import history from 'services/history';
import { booksUrl } from 'services/route';

// app: queries
import GetBookShelfQuery from 'components/BookShelf/GetBookShelf.gql';
import GetBookCategoryQuery from 'components/Books/GetBookCategoryQuery.gql';

// assets
import CssBookGrid from './BookGrid.scss';

const pageSize = 25;

function BookGrid(props) {
  const {
    category,
    includeAlacarte,
    orderBy,
    orderByDirection,
    searchQuery,
    tab,
    viewType,
  } = props;
  useStyles(CssBookGrid);

  const [alacarteEnabled] = useFeatureToggle('alacarte');

  const userId = useSelector(state => state.user.id);
  const country = useSelector(state => state.user.geolocation.countryCode);
  const includeTags = useSelector(state =>
    state.user.oven_brands?.map(brand => brand.toLowerCase()),
  );
  const displayType = useSelector(state => state.dimensions.displayType);
  const ownedBooks = useSelector(state => state.user.owned_books);

  let itemWidth;
  if (viewType === 'grid') {
    itemWidth = displayType === 'mobile' ? 125 : 200;
  } else {
    itemWidth = 125;
  }
  const itemHeight = itemWidth * (4 / 3);

  const initialIndex =
    history.location.state && history.location.state.listIndex
      ? history.location.state.listIndex
      : 0;
  const initialLimit = initialIndex ? Math.ceil(initialIndex / pageSize) * pageSize : pageSize;
  const { data: categoryData, previousData: previousCategoryData, error: categoryError } = useQuery(
    GetBookCategoryQuery,
    {
      variables: {
        id: category,
      },
      fetchPolicy: isApp() ? 'cache-and-network' : 'cache-first',
      errorPolicy: 'all',
    },
  );

  const categorySettings = categoryData?.Category || previousCategoryData?.Category || {};
  const { data, fetchMore, error, loading } = useQuery(GetBookShelfQuery, {
    variables: {
      offset: 0,
      limit: initialLimit,
      includeAlacarte: alacarteEnabled && (includeAlacarte || categorySettings.include_alacarte),
      includeTags,
      orderBy: orderBy || categorySettings.order_by,
      orderByDirection: orderBy ? orderByDirection : categorySettings.order_by_direction,
      filters: categorySettings.filters?.map(filter => ({
        field: filter.field,
        values:
          categorySettings.user_specific && filter.field.startsWith('user.')
            ? [`${userId}`]
            : filter.values,
      })),
      query: searchQuery || categorySettings.query,
      country,
      _authenticated: categorySettings.user_specific,
    },
    fetchPolicy: isApp() || categorySettings.user_specific ? 'cache-and-network' : 'cache-first',
    errorPolicy: 'all',
    skip: !categorySettings.id || (categorySettings.user_specific && userId < 1),
    ssr: categorySettings.id && !categorySettings.user_specific,
  });

  const loadMore = () => {
    let itemsToLoad;
    if (viewType === 'grid') {
      // We estimate the number of items to fill a screen from the scren dimensions and then round up
      // to the nearest 10 to improve cacheiness.
      itemsToLoad =
        Math.ceil(((window.innerWidth / itemWidth - 1) * (window.innerHeight / itemHeight)) / 10) *
        10;
    } else {
      itemsToLoad = Math.ceil(((window.innerWidth / 345) * (window.innerHeight / 100)) / 10) * 10;
    }
    fetchMore({ variables: { offset: data.Books.length, limit: itemsToLoad } });
  };

  const [endOfPageRef] = useInViewRef(
    events => {
      const { isIntersecting } = events[0];
      if (data && !loading && isIntersecting) {
        loadMore();
      }
    },
    { rootMargin: '0px 0px 350px 0px' },
  );

  useEffect(
    () => {
      if (initialIndex) {
        const el = document.querySelector(`[data-index="${initialIndex}"]`);
        if (el) {
          el.scrollIntoView(false);
        }
      }

      if (!loading && !searchQuery && !categorySettings.query) {
        window.dispatchEvent(new Event('requestEnded'));
      }
    },
    [initialIndex, loading, searchQuery, categorySettings],
  );

  const categoryTitle = categorySettings?.title;
  const renderItem = useCallback(
    index => {
      const book = data ? data.Books[index] : null;
      const handleItemClick = listIndex => {
        history.replace(
          booksUrl({ tab, category, viewType, orderBy, orderByDirection, query: searchQuery }),
          {
            listIndex,
          },
        );
      };
      const owned = ownedBooks?.includes(book.isbn);
      if (viewType === 'list') {
        return (
          <BookListTile
            backText={categoryTitle}
            item={book}
            onClick={() => handleItemClick(index)}
            owned={owned}
            withMenu
          />
        );
      }
      return (
        <BookTile
          backText={categoryTitle}
          className="book-grid__tile"
          height={itemHeight}
          lazy
          onClick={() => handleItemClick(index)}
          owned={owned}
          width={itemWidth}
          {...book}
        />
      );
    },
    [
      data,
      itemWidth,
      itemHeight,
      viewType,
      category,
      categoryTitle,
      tab,
      orderBy,
      orderByDirection,
      ownedBooks,
      searchQuery,
    ],
  );

  if (categoryError) {
    return <ErrorRouter error={categoryError} />;
  }
  if (error && !data?.Books) {
    return <ErrorRouter error={error} />;
  }
  const gridClassName = classNames('book-grid', {
    'book-grid--as-list': viewType === 'list',
    'book-grid--as-grid': viewType === 'grid',
  });
  return (
    <div className="book-grid-page">
      {categorySettings?.id &&
      categorySettings?.user_specific &&
      (data?.Books?.length === 0 || error) &&
      !loading ? (
        <EmptyShelf height={300} />
      ) : null}

      <div className={gridClassName}>
        {data?.Books.map((book, i) => (
          <div key={book.isbn} data-index={i} className="book-grid__item">
            {renderItem(i)}
          </div>
        ))}
      </div>
      <div ref={endOfPageRef} />
    </div>
  );
}

BookGrid.propTypes = {
  category: PropTypes.string,
  includeAlacarte: PropTypes.bool,
  orderBy: PropTypes.string,
  orderByDirection: PropTypes.string,
  searchQuery: PropTypes.string,
  tab: PropTypes.string,
  viewType: PropTypes.oneOf(['grid', 'list']),
};

BookGrid.defaultProps = {
  category: null,
  includeAlacarte: false,
  orderBy: null,
  orderByDirection: null,
  searchQuery: '',
  tab: null,
  viewType: 'grid',
};

export default BookGrid;
